import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Divider,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CodeIcon from '@mui/icons-material/Code';
import { TradingLevels } from '../types/trading';

interface PineScriptModalProps {
  open: boolean;
  onClose: () => void;
  pineScript: string;
  tradingLevels: TradingLevels | null;
  assetName?: string;
}

const PineScriptModal: React.FC<PineScriptModalProps> = ({
  open,
  onClose,
  pineScript,
  tradingLevels,
  assetName = 'Trading Asset'
}) => {
  const theme = useTheme();
  const [copySuccess, setCopySuccess] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(pineScript);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([pineScript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${assetName.toLowerCase().replace(/\s+/g, '-')}-trading-levels.pine`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setDownloadSuccess(true);
  };

  const formatPrice = (price: number) => {
    return price >= 1000 ? price.toFixed(2) : price.toFixed(2);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'background.paper',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : undefined
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon color="primary" />
            <Typography variant="h6" component="div">
              {assetName} - Pine Script
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {/* Trading Levels Summary */}
          {tradingLevels && (
            <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                📊 Extracted Trading Levels
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Resistance Levels */}
                <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                    🔴 Resistance Levels
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {tradingLevels.resistanceLevels.map((level, index) => (
                      <Chip
                        key={index}
                        label={`$${formatPrice(level)}`}
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>

                {/* Support Levels */}
                <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                    💚 Support Levels
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {tradingLevels.supportLevels.map((level, index) => (
                      <Chip
                        key={index}
                        label={`$${formatPrice(level)}`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>
              </Box>

              {/* Swing Points */}
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`Swing High: $${formatPrice(tradingLevels.swingHigh)}`}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`Swing Low: $${formatPrice(tradingLevels.swingLow)}`}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`Range: $${formatPrice(tradingLevels.swingHigh - tradingLevels.swingLow)}`}
                  color="secondary"
                  variant="outlined"
                />
              </Box>
            </Box>
          )}

          <Divider />

          {/* Pine Script Code */}
          <Box sx={{ p: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 2 
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                📝 Pine Script Code (TradingView)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Copy to Clipboard">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyToClipboard}
                  >
                    Copy
                  </Button>
                </Tooltip>
                <Tooltip title="Download .pine File">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </Tooltip>
              </Box>
            </Box>

            <Paper
              elevation={1}
              sx={{
                p: 2,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'grey.100',
                maxHeight: 400,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.4,
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.300',
              }}
            >
              <pre style={{ 
                margin: 0, 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                color: theme.palette.text.primary
              }}>
                {pineScript}
              </pre>
            </Paper>
          </Box>

          {/* Usage Instructions */}
          <Box sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,100,200,0.1)' : 'primary.50' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
              📋 TradingView Instructions:
            </Typography>
            <Typography variant="body2" component="div" sx={{ color: 'text.secondary' }}>
              <ol style={{ margin: 0, paddingLeft: '1.2em' }}>
                <li>Copy the Pine Script code above</li>
                <li>Go to <strong>TradingView.com</strong></li>
                <li>Open the Pine Editor (<kbd>Alt + E</kbd>)</li>
                <li>Paste the code and click <strong>"Add to Chart"</strong></li>
                <li>The levels will automatically display on your {assetName} chart</li>
              </ol>
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbars */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopySuccess(false)}>
          Pine Script copied to clipboard!
        </Alert>
      </Snackbar>

      <Snackbar
        open={downloadSuccess}
        autoHideDuration={3000}
        onClose={() => setDownloadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setDownloadSuccess(false)}>
          Pine Script file downloaded!
        </Alert>
      </Snackbar>
    </>
  );
};

export default PineScriptModal;